/**
 * ████████╗ ██████╗ ██╗     ██╗███╗   ██╗ ██████╗
 * ╚══██╔══╝██╔═══██╗██║     ██║████╗  ██║██╔═══██╗
 *    ██║   ██║   ██║██║     ██║██╔██╗ ██║██║   ██║
 *    ██║   ██║   ██║██║     ██║██║╚██╗██║██║   ██║
 *    ██║   ╚██████╔╝███████╗██║██║ ╚████║╚██████╔╝
 *    ╚═╝    ╚═════╝ ╚══════╝╚═╝╚═╝  ╚═══╝ ╚═════╝
 *
 * (c) Copyright 2021-present Rakuten Kobo Inc. (https://www.kobo.com)
 *
 * Note: The actual SVG icons should be part of a theme, but for now, we
 *       include them as a global static file.
 */

// -----------------------------------------------------------------------------
// IMPORTS
// -----------------------------------------------------------------------------
// External/Third-party dependencies
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

// Internal dependencies
import { firstToUpperCase } from '@rakuten/common-util/string';
import { ThemeContext } from '@rakuten/contexts/ThemeContext';
import useStyleSystem from '@rakuten/hooks/useStyleSystem';
import { View } from '@rakuten/modules-core-components/';
import { isStringValidator, onPressValidator } from '@resources/extensions/propTypes';
import Icon, { COLOR_VARIANT } from "../atoms/Icon";
import useRippleEffect from '@hooks/useRippleEffect';


// -----------------------------------------------------------------------------
// CONFIGURATION
// -----------------------------------------------------------------------------

/**
 * Name for icon sizes to make them more usable/recognizable.
 *
 * @type {object}
 */
const SIZE = {
  DEFAULT: 'default',
  LARGE: 'large',
  SMALL: 'small'
};



/**
 * Map of identifiers for variants of the icon display
 *
 * @type {Object}
 */
const DISPLAY_VARIANT = {
  STANDARD_PRIMARY: 'standardPrimary',
  STANDARD_GREY: 'standardGrey',
  STANDARD_FILLED: "standardFilled",
  STANDARD_NO_ICON: "standardNoIcon",
  CONTAINED_PRIMARY: "containedPrimary",
  CONTAINED_GREY: "containedGrey",
  CONTAINED_ZOOMUI_FLOATING: "containedZoomuiFloating",
  CONTAINED_ZOOMUI_NAVBAR: "containedZoomuiNavbar",
  OUTLINED_PRIMARY: "outlinedPrimary",
  OUTLINED_GREY: 'outlinedGrey',
  DARK: "dark"
};


/**
 * Identifiers for Selection Controls ( e.g. CheckBox, Switch, RadioButton )
 *
 * @type {Object}
 */
const SELECTION_CONTROL = {
  SELECTED: "selectionControlSelected",
  NOT_SELECTED: "selectionControl"
};

const selectionControlToStyle = {
  [SELECTION_CONTROL.NOT_SELECTED]: "selectionControl",
  [SELECTION_CONTROL.SELECTED]: "selectionControlSelected"
};

// -----------------------------------------------------------------------------
// STYLES
// -----------------------------------------------------------------------------

/**
 * Calculate styles for this component, optionally based on props, theme,
 * currently matching breakpoint etc.
 *
 * @param {object} args Arguments used for style calculation
 * @param {object} args.props Current props passed to the component
 * @param {object} args.theme Current theme object
 * @return {object} Object that maps style names to style objects
 */
function createStyles({ props, theme }) {
  const { colors, variables, mixins } = theme;
  const styleSelectionControl = {
    borderRadius: "50%",
    margin: "0 " + mixins.basicUnit(1)
  };

  const activeVariantContainedThemes = {
    outline: "2px solid " + colors.icon,
    border: "2px solid",
    // To avoid the double border/offset when activating a theme button via keyboard:
    // in that case theme button has focus and is activated
    outlineOffset: "0px !important"
  }

  // To avoid shrinking icons if a border is added
  const paddingWithBorder = {
    padding: variables.iconButtonSizePadding.activeWithBorder + " !important",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  };

  return {
    icon: {
      zIndex: 1,
      display: "flex",
      height: "100%",
      width: "100%"
    },

    focusStyle: {
      borderRadius: "50%",
      ":focus-visible": {
        outline: "2px solid " + theme.colors.icon,
        outlineOffset: '2px'
      }
    },

    // This extra property would usually not be necessary, the hover styles
    // should be added to the `icon` property above. But adding the styles
    // above breaks other styles
    clickable: {
      ':hover': {
        cursor: 'pointer'
      }
    },

    // fill color is valid for all iconButtons except zoom ui -floating and -navbar
    disabled: {
      fill: colors.iconDisabled,
      pointerEvents: 'none'
    },

    // used for zoom ui -floating and -navbar buttons as they have different fill colors
    // we don't use 'disabled' class for them in applied styles
    disabledPointerEvent: {
      pointerEvents: 'none'
    },

    cursorNotAllowed: {
      cursor: 'not-allowed'
    },

    // Standard icon button

    displayVariantStandardPrimary: {
      fill: props.disabled ? colors.iconDisabled : colors.primary,
      backgroundColor: colors.transparent,
      ":hover": {
        backgroundColor: props.disabled ? colors.transparent : colors.primaryHover
      },
      ":focus-visible": {
        backgroundColor: props.disabled ? colors.transparent : colors.primaryHover
      }
    },
    activeVariantStandardPrimary: {
      fill: colors.background,
      backgroundColor: colors.primary,
      pointerEvents: "none",
      ":hover": {
        fill: colors.background,
        backgroundColor: colors.primary
      },
      ":focus-visible": {
        backgroundColor: props.disabled ? colors.transparent : colors.primary
      }
    },

    displayVariantStandardGrey: {
      fill: props.disabled ? colors.iconDisabled : colors.icon,
      backgroundColor: colors.transparent,
      ":hover": {
        fill: props.disabled ? colors.iconDisabled : colors.background,
        backgroundColor: props.disabled ? colors.transparent : colors.icon
      },
      ":focus-visible": {
        backgroundColor: props.disabled ? colors.transparent : colors.iconHover
      },
      ":active": {
        backgroundColor: mixins.colorOverlay(colors.icon, colors.iconPressedInverse)
      }
    },
    activeVariantStandardGrey: {
      fill: colors.background,
      backgroundColor: colors.icon,
      pointerEvents: "none",
      ":hover": {
        fill: colors.background,
        backgroundColor: colors.icon
      },
      ":focus-visible": {
        backgroundColor: props.disabled ? colors.transparent : colors.icon
      }
    },

    displayVariantStandardFilled: {
      fill: props.disabled ? colors.iconDisabled : colors.iconFilled,
      backgroundColor: colors.transparent
    },

    displayVariantStandardNoIcon: {
      backgroundColor: colors.transparent,
      ":focus-visible": {
        outline: 'none !important'
      }
    },

    // Contained icon button

    displayVariantContainedPrimary: {
      fill: props.disabled ? colors.iconDisabled : colors.background,
      backgroundColor: props.disabled ? colors.iconHover : colors.primary,
      ":hover": {
        fill: colors.background,
        backgroundColor: props.disabled ? colors.transparent : mixins.colorOverlay(colors.primary, colors.iconHover)
      }
    },
    activeVariantContainedPrimary: {
      fill: colors.background,
      backgroundColor: colors.icon,
      pointerEvents: "none",
      ":hover": {
        fill: colors.background,
        backgroundColor: colors.icon
      }
    },

    displayVariantContainedGrey: {
      fill: props.disabled ? colors.iconDisabled : colors.icon,
      backgroundColor: props.disabled ? colors.iconHover : colors.iconHover,
      ":hover": {
        fill: colors.icon,
        backgroundColor: props.disabled ? colors.transparent : mixins.colorOverlay(colors.iconHover, colors.iconHover)
      }
    },
    activeVariantContainedGrey: {
      fill: colors.background,
      backgroundColor: colors.icon,
      pointerEvents: "none",
      ":hover": {
        fill: colors.background,
        backgroundColor: colors.icon
      }
    },


    // contained zoomui icons
    // !important for disabled fill colors to override disabled css class
    displayVariantContainedZoomuiFloating: {
      fill: props.disabled ? colors.lightIconDisabled : colors.lightIcon,
      backgroundColor: props.disabled ? mixins.colorOverlay(colors.themeBackground.light, colors.lightIconPressed) : colors.themeBackground.light,
      boxShadow: mixins.boxShadow(0.5, 1), //self defined values

      ":hover": {
        backgroundColor: props.disabled ? colors.transparent : mixins.colorOverlay(colors.themeBackground.light, colors.lightIconHover)
      },

      ":focus-visible": {
        backgroundColor: props.disabled ? colors.transparent : mixins.colorOverlay(colors.themeBackground.light, colors.lightIconHover)
      }
    },

    displayVariantContainedZoomuiNavbar: {
      ...mixins.zoomUIStylesNavBar(props.disabled)
    },

    // Outlined icon button

    displayVariantOutlinedPrimary: {
      backgroundColor: colors.transparent,
      fill: colors.primary,
      outline: `${mixins.basicUnit(0.125)} solid ${colors.primary}`,
      ":hover": {
        fill: colors.primary
      }
    },

    displayVariantOutlinedGrey: {
      ...paddingWithBorder,
      ...activeVariantContainedThemes,
      backgroundColor: colors.icon,
      border: `${mixins.basicUnit(0.1875)} solid ${colors.background}`,
      fill: colors.background,
      outline: `${mixins.basicUnit(0.1875)} solid ${colors.transparent}`,
      padding: `${variables.iconButtonSizePadding.navigationControls} !important`,
      position: 'relative'
    },

    displayVariantOutlinedGreyIcon: {
      background: colors.transparent,
      borderRadius: '50%'
    },

    // Dark icon button
    displayVariantDark: {
      fill: props.disabled ? colors.iconDisabled : colors.white,
      backgroundColor: props.disabled ? colors.iconHover : colors.transparent,
      ":hover": {
        fill: colors.black,
        backgroundColor: props.disabled ? colors.transparent : colors.white
      },

      ":active": {
        backgroundColor: mixins.colorOverlay(colors.white, colors.black16)
      },

      ":focus-visible": {
        outline: `${mixins.basicUnit(0.25)} solid ${colors.white}`,
        fill: colors.black,
        backgroundColor: props.disabled ? colors.transparent : colors.white
      }
    },

    touchAreaSize: {
      height: variables.iconSizeTouch,
      minWidth: props.biggerTouchArea ? variables.iconSizeTouchBigger : variables.iconSizeTouch,
      width: variables.iconSizeTouch,
      display: 'flex',
      alignItems: 'center',
      justifyContent: props.biggerTouchArea ? 'end' : 'center',
      cursor: 'inherit',
      position: "relative"
    },

    size: {
      height: variables.iconSizeContainer,
      minWidth: variables.iconSizeContainer,
      padding: variables.iconButtonSizePadding[props.size],
      width: variables.iconSizeContainer
    },

    selectionControl: {
      ...styleSelectionControl,
      ":hover": {
        backgroundColor: props.disabled ? theme.colors.transparent : theme.colors.iconHover
      }
    },

    selectionControlSelected: {
      ...styleSelectionControl,
      ":hover": {
        backgroundColor: props.disabled ? theme.colors.transparent : theme.colors.primaryHover
      }
    }
  };
}




// -----------------------------------------------------------------------------
// ICON COMPONENT
// -----------------------------------------------------------------------------

function IconButton(props, ref) {
  const { displayVariant } = props;
  const context = useContext(ThemeContext);
  const themeIsTolino = context.theme.name.toLowerCase().includes("tolino");

  const isClickable =
    props.onPress &&
    !props.disabled &&
    !props.active &&
    !props.selectionControl;

  // Set default displayVariants
  // State used to set displayVariants
  const [mappedDisplayVariantProp, setMappedDisplayVariantProp] = useState(themeIsTolino ? DISPLAY_VARIANT.STANDARD_PRIMARY : DISPLAY_VARIANT.STANDARD_GREY);


  // "Hook" this component into the theme/style system
  const [styles] = useStyleSystem(
    {
      id: 'IconButton',
      props,
      styleFn: createStyles
    },
    [
      Boolean(props.disabled) // undefined -> false to avoid unnecessary style calc
    ]
  );

  const handlePress = (e) => {
    props?.onPress(e);
  };

  const handleKeyPress = (e) => {
    props.onKeyPress?.(e);
    handlePress(e);
  };

  const { buttonRef, triggerRipple } = useRippleEffect({
    disabled: !isClickable
  });

  React.useImperativeHandle(ref, () => ({
    triggerRipple
  }))

  // Map tolino only displayVariants to their kobo/japan corresponding variants
  useEffect(() => {
    if (!themeIsTolino && displayVariant) {
      if (displayVariant === DISPLAY_VARIANT.STANDARD_PRIMARY) {
        setMappedDisplayVariantProp(DISPLAY_VARIANT.STANDARD_GREY);
        return;
      }
      if (displayVariant === DISPLAY_VARIANT.CONTAINED_PRIMARY) {
        setMappedDisplayVariantProp(DISPLAY_VARIANT.CONTAINED_GREY);
        return;
      }
      if (displayVariant === DISPLAY_VARIANT.OUTLINED_PRIMARY) {
        setMappedDisplayVariantProp(DISPLAY_VARIANT.OUTLINED_GREY);
        return;
      }
      setMappedDisplayVariantProp(displayVariant);
    }
    if (themeIsTolino && displayVariant) {
      setMappedDisplayVariantProp(displayVariant);
    }
  }, [displayVariant]);


  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------

  const appliedStyles = [
    styles.size,
    styles.focusStyle,
    styles[`displayVariant${firstToUpperCase(mappedDisplayVariantProp)}`] || mappedDisplayVariantProp,
    props.active && (styles[`activeVariant${firstToUpperCase(mappedDisplayVariantProp)}`] || styles.active),
    // zoomui icon buttons have different icon-colors
    props.disabled && styles.disabled,
    props.disabled && mappedDisplayVariantProp.toString().toLowerCase().includes('zoomui') && styles.disabledPointerEvent,
    props.selectionControl && styles[selectionControlToStyle[props.selectionControl]]
  ];

  if (isClickable) {
    appliedStyles.push(styles.clickable);
  }

  const accessibilityProps = {
    accessible: props.accessible !== undefined ? props.accessible : (props.onKeyPress || props.onPress) ? true : undefined,
    accessibilityLabel: props.accessibilityLabel,
    accessibilityHasPopup: props.accessibilityHasPopup,
    accessibilityState: props.accessibilityState,
    lang: props.lang
  };
  if (props.onPress) {
    // Check if an a11yRole is set otherwise 'imagebutton'.
    // Otherwise we may lost some deliberately set roles like: 'checkbox', 'radio'.
    accessibilityProps.accessibilityRole = props.accessibilityRole ? props.accessibilityRole : "imagebutton";
    if (props.disabled) {
      accessibilityProps.accessibilityState = { disabled: props.disabled };
    }
  } else {
    accessibilityProps.accessibilityRole = props.accessibilityRole || "image";
  }

  return (
    // Keep title on the outer view
    // to display the tooltip title for active IconButtons that have a pointerEvents: "none" applied by "appliedStyles"
    <View
      onPress={isClickable ? handlePress : undefined}
      style={[styles.touchAreaSize, props.additionalStyles]}
      title={accessibilityProps.accessibilityLabel}
      ref={buttonRef}
    >
      <View
        onKeyPress={isClickable ? handleKeyPress : undefined}
        style={appliedStyles}
        testID={props.testID}
        {...accessibilityProps}
      >
        <Icon
          icon={props.icon}
          accessible={false}
          size={props.size}
          colorVariant={COLOR_VARIANT.NONE}
          testID={props.testID}
        />
      </View>
    </View>
  );
}

const ForwardedIconButton = React.forwardRef(IconButton);


// -----------------------------------------------------------------------------
// PROPS VALIDATION
// -----------------------------------------------------------------------------

ForwardedIconButton.propTypes = {
  /** Weather the component is accessible for a11y systems or not [optional] */
  accessible: PropTypes.bool,

  /** The text for a11y systems, required if prop `onPress` is defined */
  accessibilityLabel: PropTypes.oneOfType([isStringValidator, onPressValidator]),

  /** Whether this icon is currently active or not [optional] */
  active: PropTypes.bool,

  /** Whether this icon has a bigger touch area or not [optional] */
  biggerTouchArea: PropTypes.bool,

  /** Whether or not the icon should be visualized in a disabled state [optional] */
  disabled: PropTypes.bool,

  /** The icon's display variant [optional] */
  displayVariant: PropTypes.oneOf(Object.values(DISPLAY_VARIANT)),

  /** UI language locale [optional] */
  lang: PropTypes.string,

  /** Display the icon as Selection Control [optional] */
  selectionControl: PropTypes.oneOf(Object.values(SELECTION_CONTROL)),

  /** The icon name/identifier */
  icon: PropTypes.string.isRequired,

  /** A function that is executed when the icon is pressed/tapped [optional] */
  onPress: PropTypes.func,

  /** The size of the icon [optional] */
  size: PropTypes.oneOf(Object.values(SIZE)),

  /** ID for test automation [optional] */
  testID: PropTypes.string,

  /** A list of additional styles to be added to the icon [optional] */
  additionalStyles: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.string
  ])
};

ForwardedIconButton.defaultProps = {
  active: false,
  size: SIZE.DEFAULT,
  testID: null
};


// -----------------------------------------------------------------------------
// EXPORTS
// -----------------------------------------------------------------------------

export default ForwardedIconButton;
export {
  DISPLAY_VARIANT,
  SIZE,
  SELECTION_CONTROL
};
